import React from "react";
import { useBuddyInvites } from "../context/BuddyInvitesContext";
import { Button } from "./ui/button";
import { Users, Check, X } from "lucide-react";
import { toast } from "sonner";

/**
 * Pending buddy-invite banner. Renders at the top of the Dashboard so a new user
 * can accept/decline without ever navigating to the Squad → Buddies tab.
 * Hidden when there are no pending invites.
 */
export default function BuddyInvitesBanner() {
  const { incoming, accept, decline } = useBuddyInvites();
  if (!incoming || incoming.length === 0) return null;

  const handleAccept = async (b) => {
    try {
      await accept(b.request_id);
      toast.success(`${b.from_name} is now your gym buddy`);
    } catch (e) {
      toast.error("Couldn't accept — try again");
    }
  };
  const handleDecline = async (b) => {
    try {
      await decline(b.request_id);
      toast(`Declined ${b.from_name}`);
    } catch {
      toast.error("Couldn't decline — try again");
    }
  };

  return (
    <div data-testid="buddy-invites-banner" className="border border-blue-500/40 bg-blue-500/5 mb-5">
      <div className="flex items-center gap-2 px-4 py-2 border-b border-blue-500/20 text-blue-500 text-xs tracking-[0.3em] uppercase font-bold">
        <Users className="w-3 h-3" />
        {incoming.length === 1 ? "New gym buddy request" : `${incoming.length} new gym buddy requests`}
      </div>
      <div className="divide-y divide-blue-500/10">
        {incoming.map((b) => (
          <div
            key={b.request_id}
            data-testid={`invite-row-${b.request_id}`}
            className="flex items-center justify-between gap-2 px-4 py-3"
          >
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 bg-blue-500 flex items-center justify-center font-display text-sm shrink-0">
                {b.from_name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="min-w-0">
                <div className="font-semibold truncate">{b.from_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">wants to train with you</div>
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <Button
                size="sm"
                data-testid={`banner-accept-${b.request_id}`}
                onClick={() => handleAccept(b)}
                className="bg-blue-500 hover:bg-blue-600 rounded-none uppercase tracking-wider font-display h-8"
              >
                <Check className="w-3 h-3 mr-1" /> Accept
              </Button>
              <Button
                size="icon"
                variant="ghost"
                data-testid={`banner-decline-${b.request_id}`}
                onClick={() => handleDecline(b)}
                className="text-neutral-400 hover:text-blue-500 h-8 w-8"
                title="Decline"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
